import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import {
  Car, Search, Plus, ChevronRight, ChevronLeft, User, Phone, Mail, MapPin, Calendar,
  FileText, Loader2, CheckCircle2, XCircle, Star, ArrowRight, Filter, BarChart3,
  Clock, Send, UserPlus, Eye, RefreshCw, AlertTriangle, Shield, Fuel, Gauge, Hash,
  Settings2, List, ArrowUpDown, ChevronDown, Building2, Tag
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import GestioneCompagnie from './GestioneCompagnie';

const STATUS_COLORS = {
  nuova_richiesta: 'bg-blue-100 text-blue-700',
  dati_incompleti: 'bg-amber-100 text-amber-700',
  dati_veicolo_recuperati: 'bg-cyan-100 text-cyan-700',
  in_elaborazione: 'bg-indigo-100 text-indigo-700',
  preventivi_acquisiti: 'bg-purple-100 text-purple-700',
  filtrata_internamente: 'bg-slate-100 text-slate-700',
  in_quotazione_definitiva: 'bg-violet-100 text-violet-700',
  preventivo_inviato: 'bg-emerald-100 text-emerald-700',
  da_richiamare: 'bg-orange-100 text-orange-700',
  trattativa_aperta: 'bg-yellow-100 text-yellow-800',
  emessa: 'bg-green-100 text-green-700',
  persa: 'bg-red-100 text-red-700',
  sospesa: 'bg-gray-100 text-gray-600',
};

const STATUSES = [
  { value: 'nuova_richiesta', label: 'Nuova Richiesta' },
  { value: 'dati_incompleti', label: 'Dati Incompleti' },
  { value: 'dati_veicolo_recuperati', label: 'Dati Veicolo OK' },
  { value: 'in_elaborazione', label: 'In Elaborazione' },
  { value: 'preventivi_acquisiti', label: 'Preventivi Acquisiti' },
  { value: 'filtrata_internamente', label: 'Filtrata' },
  { value: 'in_quotazione_definitiva', label: 'Quotazione Definitiva' },
  { value: 'preventivo_inviato', label: 'Preventivo Inviato' },
  { value: 'da_richiamare', label: 'Da Richiamare' },
  { value: 'trattativa_aperta', label: 'Trattativa Aperta' },
  { value: 'emessa', label: 'Emessa' },
  { value: 'persa', label: 'Persa' },
  { value: 'sospesa', label: 'Sospesa' },
];

const CU_CLASSES = Array.from({ length: 18 }, (_, i) => i + 1);

const EMPTY_FORM = {
  vehicle_type: 'autovettura',
  operation_type: 'rinnovo',
  plate: '',
  tax_code: '',
  bonus_famiglia: false,
  make: '',
  model: '',
  version: '',
  fuel_type: '',
  power_kw: '',
  displacement_cc: '',
  registration_date: '',
  registration_year: '',
  usage_type: 'Privato',
  annual_mileage: '10000',
  purchase_date: '',
  birth_date: '',
  marital_status: '',
  education: '',
  profession: '',
  children: '0',
  youngest_driver_age: '',
  licensed: true,
  household_cars: '1',
  province: '',
  city: '',
  address: '',
  street_number: '',
  zip_code: '',
  first_name: '',
  last_name: '',
  phone: '',
  email: '',
  policy_start_date: '',
  cu_class: 14,
  claims_last_5y: '0',
  current_company: '',
  current_policy_expiry: '',
  renewal_same_holder: true,
  bersani_applied: false,
  guida_esperta: false,
  guida_esclusiva: false,
  black_box: false,
  pagamento_rate: false,
  notes_internal: '',
};

// ── Field component (OUTSIDE QuoteWizard to prevent re-mount on every keystroke) ──
const Field = ({ label, value, onChange, type = 'text', placeholder, className = '', required, disabled }) => (
  <div className={className}>
    <label className="block text-[11px] font-medium text-slate-500 mb-1">
      {label}{required && <span className="text-red-400 ml-0.5">*</span>}
    </label>
    <Input
      type={type}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="h-9 text-sm"
      disabled={disabled}
    />
  </div>
);

const Checkbox = ({ label, checked, onChange, description }) => (
  <label className="flex items-start gap-2.5 cursor-pointer group py-1">
    <input
      type="checkbox"
      checked={checked || false}
      onChange={e => onChange(e.target.checked)}
      className="rounded border-slate-300 mt-0.5 accent-blue-600"
    />
    <div>
      <span className="text-sm text-slate-700 group-hover:text-slate-900">{label}</span>
      {description && <p className="text-[10px] text-slate-400 mt-0.5">{description}</p>}
    </div>
  </label>
);

const SectionTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-100">
    {Icon && <Icon size={15} className="text-blue-600" />}
    <h4 className="text-sm font-bold text-slate-800">{children}</h4>
  </div>
);

// ══════════════════════════════════════
//  WIZARD COMPONENT - 6 STEP
// ══════════════════════════════════════
const QuoteWizard = ({ onComplete, onCancel }) => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);
  const [lookingUp, setLookingUp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lookupResult, setLookupResult] = useState(null);
  const [results, setResults] = useState(null);
  const [sortBy, setSortBy] = useState('price_asc');
  const [filterCompany, setFilterCompany] = useState('');
  const [filterUsable, setFilterUsable] = useState(false);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const lookupPlate = async () => {
    if (!form.plate || form.plate.length < 5) return;
    setLookingUp(true);
    setLookupResult(null);
    try {
      const params = new URLSearchParams();
      if (form.tax_code) params.append('tax_code', form.tax_code);
      params.append('vehicle_type', form.vehicle_type);
      params.append('operation_type', form.operation_type);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const res = await api.get(`/auto-quotes/vehicle-by-plate/${form.plate}${qs}`);
      const d = res.data;
      setLookupResult(d);
      if (d.found) {
        setForm(prev => ({
          ...prev,
          make: d.make || prev.make,
          model: d.model || prev.model,
          version: d.version || prev.version,
          fuel_type: d.fuel_type || prev.fuel_type,
          power_kw: d.power_kw || prev.power_kw,
          displacement_cc: d.displacement_cc || prev.displacement_cc,
          registration_date: d.registration_date || prev.registration_date,
          registration_year: d.registration_year || prev.registration_year,
          current_company: d.current_insurer || prev.current_company,
          current_policy_expiry: d.policy_expiry || prev.current_policy_expiry,
          cu_class: d.cu_class || prev.cu_class,
          claims_last_5y: d.claims_last_5y !== undefined && d.claims_last_5y !== null ? String(d.claims_last_5y) : prev.claims_last_5y,
        }));
        const parts = [];
        if (d.found_vehicle) parts.push('veicolo');
        if (d.found_insurance) parts.push('assicurazione');
        if (d.found_attestato) parts.push('attestato rischio');
        toast.success(parts.length > 0 ? `Trovati: ${parts.join(', ')}` : 'Dati parziali trovati', { duration: 4000 });
      } else {
        toast.info('Dati non trovati, inserisci manualmente', { duration: 3000 });
      }
    } catch { toast.error('Errore recupero dati'); }
    finally { setLookingUp(false); }
  };

  const handleSubmit = async () => {
    if (!form.plate) { toast.error('Inserisci almeno la targa'); return; }
    setSubmitting(true);
    setResults(null);
    try {
      const payload = {
        customer: {
          first_name: form.first_name,
          last_name: form.last_name,
          tax_code: form.tax_code,
          birth_date: form.birth_date,
          marital_status: form.marital_status,
          education: form.education,
          profession: form.profession,
          children: parseInt(form.children) || 0,
          youngest_driver_age: form.youngest_driver_age,
          licensed: form.licensed,
          household_cars: parseInt(form.household_cars) || 1,
          province: form.province,
          city: form.city,
          address: form.address,
          street_number: form.street_number,
          zip_code: form.zip_code,
          phone: form.phone,
          email: form.email,
        },
        vehicle: {
          plate: form.plate,
          vehicle_type_category: form.vehicle_type,
          make: form.make,
          model: form.model,
          version: form.version,
          fuel_type: form.fuel_type,
          power_kw: parseInt(form.power_kw) || 0,
          displacement_cc: parseInt(form.displacement_cc) || 0,
          registration_date: form.registration_date,
          registration_year: parseInt(form.registration_year) || 0,
          usage_type: form.usage_type,
          purchase_date: form.purchase_date,
        },
        quote_params: {
          operation_type: form.operation_type,
          policy_start_date: form.policy_start_date,
          cu_class: form.cu_class,
          claims_last_5y: parseInt(form.claims_last_5y) || 0,
          current_company: form.current_company,
          current_policy_expiry: form.current_policy_expiry,
          renewal_same_holder: form.renewal_same_holder,
          bersani_applied: form.bersani_applied,
          bonus_famiglia: form.bonus_famiglia,
          annual_mileage: parseInt(form.annual_mileage) || 10000,
        },
        clauses: {
          guida_esperta: form.guida_esperta,
          guida_esclusiva: form.guida_esclusiva,
          black_box: form.black_box,
          pagamento_rate: form.pagamento_rate,
        },
      };
      const res = await api.post('/auto-quotes/preventivass-quote-direct', payload);
      if (res.data) {
        setResults(res.data);
        setStep(6);
        if (res.data.success && res.data.quotes?.length > 0) {
          toast.success(`${res.data.quotes.length} preventivi reali trovati!`);
        } else {
          toast.error(res.data.error || 'Microservizio Preventivass non disponibile');
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore di comunicazione con il server');
    }
    finally { setSubmitting(false); }
  };

  const steps = [
    { title: 'Veicolo', icon: Car, short: 'Veicolo' },
    { title: 'Dati Veicolo', icon: Settings2, short: 'Dettagli' },
    { title: 'Assicurato', icon: User, short: 'Assicurato' },
    { title: 'Polizza', icon: Shield, short: 'Polizza' },
    { title: 'Clausole', icon: FileText, short: 'Clausole' },
    { title: 'Riepilogo', icon: CheckCircle2, short: 'Riepilogo' },
  ];

  // Get sorted/filtered results - SOLO dati reali da Preventivass
  const getDisplayResults = () => {
    if (!results || !results.success || !results.quotes?.length) return [];
    let items = results.quotes.map((q, i) => ({
      company: q.company || 'N/D',
      price: q.premium || 0,
      discount: q.discount || '',
      clauses: q.clauses || [],
      directCompensation: q.directCompensation ?? false,
      position: q.position || i + 1,
      is_usable: q.is_usable !== false,
      has_convention: q.has_convention || false,
      convention_name: q.convention_name || '',
      discount_code: q.discount_code || '',
      priority: q.priority || 99,
      is_recommended: q.is_recommended || false,
    }));
    if (filterCompany) {
      items = items.filter(i => i.company.toLowerCase().includes(filterCompany.toLowerCase()));
    }
    if (filterUsable) {
      items = items.filter(i => i.is_usable);
    }
    if (sortBy === 'price_asc') items.sort((a, b) => a.price - b.price);
    else if (sortBy === 'price_desc') items.sort((a, b) => b.price - a.price);
    else if (sortBy === 'name') items.sort((a, b) => a.company.localeCompare(b.company));
    else if (sortBy === 'priority') items.sort((a, b) => a.priority - b.priority || a.price - b.price);
    return items;
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="quote-wizard">
      {/* Step indicator */}
      {step < 6 && (
        <div className="bg-slate-50 border-b border-slate-200 px-3 sm:px-6 py-3">
          <div className="flex items-center gap-1 sm:gap-2 justify-center flex-wrap">
            {steps.map((s, i) => (
              <React.Fragment key={i}>
                <button
                  onClick={() => i <= step ? setStep(i) : null}
                  data-testid={`wizard-step-${i}`}
                  className={`flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all ${
                    i === step ? 'bg-blue-600 text-white shadow-sm' :
                    i < step ? 'bg-emerald-100 text-emerald-700 cursor-pointer hover:bg-emerald-200' :
                    'bg-white text-slate-400 border border-slate-200'
                  }`}>
                  {i < step ? <CheckCircle2 size={12} /> : <s.icon size={12} />}
                  <span className="hidden sm:inline">{s.title}</span>
                  <span className="sm:hidden">{s.short}</span>
                </button>
                {i < steps.length - 1 && <ChevronRight size={12} className="text-slate-300 hidden sm:block" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      <div className="p-4 sm:p-6">
        {/* ── STEP 0: Targa + Tipo Veicolo ── */}
        {step === 0 && (
          <div data-testid="step-0-vehicle-type">
            <SectionTitle icon={Car}>Tipo Veicolo e Targa</SectionTitle>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">Tipo Veicolo</label>
                <Select value={form.vehicle_type} onValueChange={v => set('vehicle_type', v)}>
                  <SelectTrigger className="h-9 text-sm" data-testid="vehicle-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="autovettura">Autovettura</SelectItem>
                    <SelectItem value="motociclo">Motociclo</SelectItem>
                    <SelectItem value="ciclomotore">Ciclomotore</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">Tipo Operazione</label>
                <Select value={form.operation_type} onValueChange={v => set('operation_type', v)}>
                  <SelectTrigger className="h-9 text-sm" data-testid="operation-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rinnovo">Rinnovo</SelectItem>
                    <SelectItem value="nuovo_acquisto">Nuova Polizza</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 mb-3">
              <Input
                value={form.tax_code}
                onChange={e => set('tax_code', e.target.value.toUpperCase())}
                placeholder="Codice Fiscale"
                className="h-10 text-sm font-mono uppercase flex-1"
                data-testid="tax-code-input"
              />
              <Input
                value={form.plate}
                onChange={e => set('plate', e.target.value.toUpperCase())}
                placeholder="Targa (es. AB123CD)"
                className="h-10 text-sm font-mono uppercase w-full sm:w-40"
                data-testid="plate-input"
              />
              <Button
                onClick={lookupPlate}
                disabled={lookingUp || !form.plate}
                className="h-10 bg-blue-600 hover:bg-blue-700 whitespace-nowrap"
                data-testid="lookup-plate-btn"
              >
                {lookingUp ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                <span className="ml-1.5">Cerca Dati</span>
              </Button>
            </div>

            {lookingUp && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3 flex items-center gap-2" data-testid="lookup-loading">
                <Loader2 size={14} className="animate-spin text-blue-600" />
                <span className="text-xs text-blue-700">Ricerca dati assicurativi in corso...</span>
              </div>
            )}

            {lookupResult && !lookingUp && (
              <div className={`rounded-lg p-3 mb-3 border text-xs ${lookupResult.found ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-testid="lookup-result">
                <div className="flex flex-wrap gap-2 items-center">
                  {lookupResult.found_insurance && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                      <CheckCircle2 size={10} /> Assicurazione
                    </span>
                  )}
                  {lookupResult.found_vehicle && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                      <CheckCircle2 size={10} /> Veicolo
                    </span>
                  )}
                  {lookupResult.found_attestato && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                      <CheckCircle2 size={10} /> Classe CU
                    </span>
                  )}
                  {!lookupResult.found && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                      <AlertTriangle size={10} /> Inserisci manualmente
                    </span>
                  )}
                </div>
                {lookupResult.current_insurer && (
                  <p className="mt-1.5 text-slate-600">Assicurato con <strong>{lookupResult.current_insurer}</strong> - Scadenza: {lookupResult.policy_expiry || '-'}</p>
                )}
              </div>
            )}

            <Checkbox label="Bonus Famiglia" checked={form.bonus_famiglia} onChange={v => set('bonus_famiglia', v)} description="Applica bonus famiglia se idoneo" />
          </div>
        )}

        {/* ── STEP 1: Dati Veicolo dettagliati ── */}
        {step === 1 && (
          <div data-testid="step-1-vehicle-data">
            <SectionTitle icon={Car}>Dati Veicolo</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <Field label="Marca" value={form.make} onChange={v => set('make', v)} placeholder="Es. FIAT" required />
              <Field label="Modello" value={form.model} onChange={v => set('model', v)} placeholder="Es. PANDA" required />
              <Field label="Allestimento" value={form.version} onChange={v => set('version', v)} placeholder="Es. 1.2 69CV" />
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">Alimentazione</label>
                <Select value={form.fuel_type || ''} onValueChange={v => set('fuel_type', v)}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Benzina">Benzina</SelectItem>
                    <SelectItem value="Diesel">Diesel</SelectItem>
                    <SelectItem value="GPL">GPL</SelectItem>
                    <SelectItem value="Metano">Metano</SelectItem>
                    <SelectItem value="Elettrica">Elettrica</SelectItem>
                    <SelectItem value="Ibrida">Ibrida</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Field label="Potenza kW" value={form.power_kw} onChange={v => set('power_kw', v)} type="number" placeholder="Es. 51" />
              <Field label="Cilindrata cc" value={form.displacement_cc} onChange={v => set('displacement_cc', v)} type="number" placeholder="Es. 1242" />
              <Field label="Data Immatricolazione" value={form.registration_date} onChange={v => set('registration_date', v)} type="date" />
              <Field label="Anno Veicolo" value={form.registration_year} onChange={v => set('registration_year', v)} type="number" placeholder="Es. 2020" />
              <Field label="Data Acquisto (mm/aaaa)" value={form.purchase_date} onChange={v => set('purchase_date', v)} placeholder="Es. 03/2020" />
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">Utilizzo Veicolo</label>
                <Select value={form.usage_type} onValueChange={v => set('usage_type', v)}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Privato">Privato</SelectItem>
                    <SelectItem value="Promiscuo">Promiscuo</SelectItem>
                    <SelectItem value="Professionale">Professionale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">Km Annui</label>
                <Select value={form.annual_mileage} onValueChange={v => set('annual_mileage', v)}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5000">fino a 5.000</SelectItem>
                    <SelectItem value="10000">fino a 10.000</SelectItem>
                    <SelectItem value="15000">fino a 15.000</SelectItem>
                    <SelectItem value="20000">fino a 20.000</SelectItem>
                    <SelectItem value="25000">fino a 25.000</SelectItem>
                    <SelectItem value="30000">fino a 30.000</SelectItem>
                    <SelectItem value="50000">oltre 30.000</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 2: Dati Assicurato ── */}
        {step === 2 && (
          <div data-testid="step-2-insured-data">
            <SectionTitle icon={User}>Dati Assicurato</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <Field label="Nome" value={form.first_name} onChange={v => set('first_name', v)} placeholder="Nome" required />
              <Field label="Cognome" value={form.last_name} onChange={v => set('last_name', v)} placeholder="Cognome" required />
              <Field label="Data di Nascita" value={form.birth_date} onChange={v => set('birth_date', v)} type="date" required />
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">Stato Civile</label>
                <Select value={form.marital_status || ''} onValueChange={v => set('marital_status', v)}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Celibe/Nubile">Celibe/Nubile</SelectItem>
                    <SelectItem value="Coniugato/a">Coniugato/a</SelectItem>
                    <SelectItem value="Convivente">Convivente</SelectItem>
                    <SelectItem value="Separato/a">Separato/a</SelectItem>
                    <SelectItem value="Divorziato/a">Divorziato/a</SelectItem>
                    <SelectItem value="Vedovo/a">Vedovo/a</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">Titolo di Studio</label>
                <Select value={form.education || ''} onValueChange={v => set('education', v)}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Licenza elementare">Licenza elementare</SelectItem>
                    <SelectItem value="Licenza media">Licenza media</SelectItem>
                    <SelectItem value="Diploma">Diploma</SelectItem>
                    <SelectItem value="Laurea">Laurea</SelectItem>
                    <SelectItem value="Post-laurea">Post-laurea</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">Professione</label>
                <Select value={form.profession || ''} onValueChange={v => set('profession', v)}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Dipendente">Dipendente</SelectItem>
                    <SelectItem value="Autonomo">Autonomo</SelectItem>
                    <SelectItem value="Libero professionista">Libero professionista</SelectItem>
                    <SelectItem value="Imprenditore">Imprenditore</SelectItem>
                    <SelectItem value="Pensionato">Pensionato</SelectItem>
                    <SelectItem value="Studente">Studente</SelectItem>
                    <SelectItem value="Casalinga">Casalinga</SelectItem>
                    <SelectItem value="Disoccupato">Disoccupato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Field label="Numero Figli" value={form.children} onChange={v => set('children', v)} type="number" placeholder="0" />
              <Field label="Eta Guidatore Piu Giovane" value={form.youngest_driver_age} onChange={v => set('youngest_driver_age', v)} type="number" placeholder="Es. 25" />
              <Field label="Veicoli in Famiglia" value={form.household_cars} onChange={v => set('household_cars', v)} type="number" placeholder="1" />
              <Field label="Telefono" value={form.phone} onChange={v => set('phone', v)} placeholder="+39..." />
              <Field label="Email" value={form.email} onChange={v => set('email', v)} type="email" placeholder="email@..." />
            </div>

            <div className="mt-4">
              <SectionTitle icon={MapPin}>Residenza</SectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <Field label="Provincia" value={form.province} onChange={v => set('province', v.toUpperCase())} placeholder="Es. CT" required />
                <Field label="Comune" value={form.city} onChange={v => set('city', v)} placeholder="Es. Catania" required />
                <Field label="Indirizzo" value={form.address} onChange={v => set('address', v)} placeholder="Via..." className="lg:col-span-2" />
                <Field label="N. Civico" value={form.street_number} onChange={v => set('street_number', v)} placeholder="Es. 10" />
                <Field label="CAP" value={form.zip_code} onChange={v => set('zip_code', v)} placeholder="Es. 95100" />
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 3: Dati Polizza ── */}
        {step === 3 && (
          <div data-testid="step-3-policy-data">
            <SectionTitle icon={Shield}>Dati Polizza</SectionTitle>
            {lookupResult?.found_insurance && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 mb-3 text-xs text-emerald-700 flex items-center gap-2">
                <CheckCircle2 size={12} />
                <span>Dati compilati dalla ricerca targa</span>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <Field label="Data Decorrenza Polizza" value={form.policy_start_date} onChange={v => set('policy_start_date', v)} type="date" required />
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">Classe CU (Merito)<span className="text-red-400 ml-0.5">*</span></label>
                <Select value={String(form.cu_class)} onValueChange={v => set('cu_class', parseInt(v))}>
                  <SelectTrigger className="h-9 text-sm" data-testid="cu-class-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CU_CLASSES.map(c => <SelectItem key={c} value={String(c)}>{c}a classe</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Field label="Sinistri Ultimi 5 Anni" value={form.claims_last_5y} onChange={v => set('claims_last_5y', v)} type="number" placeholder="0" />
              <Field label="Compagnia Attuale" value={form.current_company} onChange={v => set('current_company', v)} placeholder="Es. UnipolSai" />
              <Field label="Scadenza Polizza Attuale" value={form.current_policy_expiry} onChange={v => set('current_policy_expiry', v)} type="date" />
            </div>
            <div className="mt-4 space-y-1">
              <Checkbox label="Rinnovo con stesso contraente" checked={form.renewal_same_holder} onChange={v => set('renewal_same_holder', v)} />
              <Checkbox label="Legge Bersani" checked={form.bersani_applied} onChange={v => set('bersani_applied', v)} description="Eredita classe CU da altro veicolo familiare" />
            </div>
          </div>
        )}

        {/* ── STEP 4: Clausole ── */}
        {step === 4 && (
          <div data-testid="step-4-clauses">
            <SectionTitle icon={FileText}>Clausole Facoltative</SectionTitle>
            <p className="text-xs text-slate-500 mb-4">Seleziona le clausole da applicare al preventivo. Queste influenzeranno il prezzo finale.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                <Checkbox
                  label="Guida Esperta"
                  checked={form.guida_esperta}
                  onChange={v => set('guida_esperta', v)}
                  description="Solo conducenti con 26+ anni e patente da 2+ anni"
                />
              </div>
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                <Checkbox
                  label="Guida Esclusiva"
                  checked={form.guida_esclusiva}
                  onChange={v => set('guida_esclusiva', v)}
                  description="Solo il contraente puo guidare il veicolo"
                />
              </div>
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                <Checkbox
                  label="Scatola Nera (Black Box)"
                  checked={form.black_box}
                  onChange={v => set('black_box', v)}
                  description="Dispositivo GPS per tracciamento guida - sconto premio"
                />
              </div>
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                <Checkbox
                  label="Pagamento a Rate"
                  checked={form.pagamento_rate}
                  onChange={v => set('pagamento_rate', v)}
                  description="Frazionamento premio (semestrale o trimestrale)"
                />
              </div>
            </div>
            <div className="mt-4">
              <Field label="Note interne" value={form.notes_internal} onChange={v => set('notes_internal', v)} placeholder="Note visibili solo all'operatore..." className="w-full" />
            </div>
          </div>
        )}

        {/* ── STEP 5: Riepilogo ── */}
        {step === 5 && (
          <div data-testid="step-5-summary">
            <SectionTitle icon={CheckCircle2}>Riepilogo Preventivo</SectionTitle>
            <p className="text-xs text-slate-500 mb-4">Verifica tutti i dati prima di procedere al calcolo.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              {/* Veicolo */}
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                <h4 className="text-xs font-bold text-blue-800 mb-2 flex items-center gap-1"><Car size={13} /> Veicolo</h4>
                <div className="space-y-1 text-xs text-blue-700">
                  <p><strong>Targa:</strong> {form.plate || '-'}</p>
                  <p><strong>Tipo:</strong> {form.vehicle_type} - {form.operation_type}</p>
                  <p><strong>Veicolo:</strong> {form.make} {form.model} {form.version}</p>
                  <p><strong>Alimentazione:</strong> {form.fuel_type || '-'} | <strong>kW:</strong> {form.power_kw || '-'} | <strong>cc:</strong> {form.displacement_cc || '-'}</p>
                  <p><strong>Uso:</strong> {form.usage_type} | <strong>Km/anno:</strong> {form.annual_mileage}</p>
                </div>
              </div>

              {/* Assicurato */}
              <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100">
                <h4 className="text-xs font-bold text-emerald-800 mb-2 flex items-center gap-1"><User size={13} /> Assicurato</h4>
                <div className="space-y-1 text-xs text-emerald-700">
                  <p><strong>Nome:</strong> {form.first_name} {form.last_name}</p>
                  <p><strong>CF:</strong> {form.tax_code || '-'}</p>
                  <p><strong>Nascita:</strong> {form.birth_date || '-'}</p>
                  <p><strong>Professione:</strong> {form.profession || '-'} | <strong>Figli:</strong> {form.children}</p>
                  <p><strong>Residenza:</strong> {form.address} {form.street_number}, {form.city} ({form.province}) {form.zip_code}</p>
                  <p><strong>Tel:</strong> {form.phone || '-'} | <strong>Email:</strong> {form.email || '-'}</p>
                </div>
              </div>

              {/* Polizza */}
              <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                <h4 className="text-xs font-bold text-amber-800 mb-2 flex items-center gap-1"><Shield size={13} /> Polizza</h4>
                <div className="space-y-1 text-xs text-amber-700">
                  <p><strong>Classe CU:</strong> {form.cu_class}a</p>
                  <p><strong>Sinistri:</strong> {form.claims_last_5y} | <strong>Bersani:</strong> {form.bersani_applied ? 'Si' : 'No'}</p>
                  <p><strong>Compagnia attuale:</strong> {form.current_company || 'Prima polizza'}</p>
                  <p><strong>Decorrenza:</strong> {form.policy_start_date || '-'}</p>
                  <p><strong>Rinnovo stesso contraente:</strong> {form.renewal_same_holder ? 'Si' : 'No'}</p>
                </div>
              </div>

              {/* Clausole */}
              <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                <h4 className="text-xs font-bold text-purple-800 mb-2 flex items-center gap-1"><FileText size={13} /> Clausole</h4>
                <div className="space-y-1 text-xs text-purple-700">
                  <p>Guida Esperta: <strong>{form.guida_esperta ? 'Si' : 'No'}</strong></p>
                  <p>Guida Esclusiva: <strong>{form.guida_esclusiva ? 'Si' : 'No'}</strong></p>
                  <p>Scatola Nera: <strong>{form.black_box ? 'Si' : 'No'}</strong></p>
                  <p>Pagamento Rate: <strong>{form.pagamento_rate ? 'Si' : 'No'}</strong></p>
                  {form.notes_internal && <p className="text-purple-500 mt-1"><strong>Note:</strong> {form.notes_internal}</p>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 6: Risultati ── */}
        {step === 6 && results && (
          <div data-testid="step-6-results">
            {/* ── ERRORE: VPS non disponibile ── */}
            {!results.success && (
              <div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-5 text-center" data-testid="vps-error">
                  <XCircle size={32} className="mx-auto text-red-400 mb-3" />
                  <h3 className="text-base font-bold text-red-800 mb-2">Errore Tecnico</h3>
                  <p className="text-sm text-red-700 mb-3">
                    Il microservizio Preventivass non ha restituito risultati.
                  </p>
                  <div className="bg-red-100/50 rounded-lg p-3 text-xs text-red-600 text-left max-w-lg mx-auto mb-4">
                    <p className="font-medium mb-1">Dettaglio errore:</p>
                    <p>{results.error || 'Microservizio VPS non raggiungibile'}</p>
                  </div>
                  <p className="text-xs text-slate-500 mb-4">
                    Verifica che il microservizio sulla VPS sia attivo e raggiungibile.
                    La pratica <strong>{results.request_code}</strong> e stata salvata - potrai riprovare quando il servizio sara operativo.
                  </p>
                  <div className="flex justify-center gap-2">
                    <Button variant="outline" onClick={() => { setStep(0); setResults(null); }} className="h-9 text-xs" data-testid="retry-new-btn">
                      Nuovo Preventivo
                    </Button>
                    <Button onClick={handleSubmit} disabled={submitting} className="h-9 text-xs bg-blue-600 hover:bg-blue-700" data-testid="retry-btn">
                      {submitting ? <Loader2 size={14} className="animate-spin mr-1" /> : <RefreshCw size={14} className="mr-1" />}
                      Riprova
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* ── SUCCESSO: Preventivi reali ── */}
            {results.success && results.quotes?.length > 0 && (
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
                  <div>
                    <h3 className="text-base font-bold text-slate-900">
                      Preventivi Reali
                      <Badge className="ml-2 bg-emerald-100 text-emerald-700 text-[10px]">Preventivass</Badge>
                    </h3>
                    <p className="text-xs text-slate-500">
                      Targa: {form.plate} | {getDisplayResults().length} compagnie
                      {results.total_results ? ` su ${results.total_results} totali` : ''}
                      {' | Pratica: '}{results.request_code}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setFilterUsable(!filterUsable)}
                      className={`h-8 px-3 rounded-lg text-[11px] font-medium flex items-center gap-1.5 border transition-colors ${
                        filterUsable
                          ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                          : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'
                      }`}
                      data-testid="filter-usable-btn"
                    >
                      <Shield size={11} /> Solo utilizzabili
                    </button>
                    <div className="relative">
                      <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        value={filterCompany}
                        onChange={e => setFilterCompany(e.target.value)}
                        placeholder="Filtra compagnia..."
                        className="h-8 text-xs pl-7 w-40"
                        data-testid="filter-company"
                      />
                    </div>
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger className="h-8 w-[155px] text-[11px]" data-testid="sort-results">
                        <ArrowUpDown size={11} className="mr-1" /><SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="price_asc">Prezzo crescente</SelectItem>
                        <SelectItem value="price_desc">Prezzo decrescente</SelectItem>
                        <SelectItem value="name">Nome A-Z</SelectItem>
                        <SelectItem value="priority">Priorita interna</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="divide-y divide-slate-100">
                    {getDisplayResults().map((item, i) => (
                      <div
                        key={`${item.company}-${i}`}
                        className={`px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 transition-colors ${
                          item.is_recommended ? 'bg-emerald-50/70 border-l-3 border-l-emerald-500' :
                          !item.is_usable ? 'bg-slate-50/50 opacity-50' :
                          item.has_convention ? 'bg-purple-50/30' :
                          'hover:bg-slate-50'
                        }`}
                        data-testid={`result-row-${i}`}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <span className={`text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 ${
                            item.is_recommended ? 'bg-emerald-600 text-white' :
                            !item.is_usable ? 'bg-slate-200 text-slate-400' :
                            'bg-slate-100 text-slate-500'
                          }`}>{i + 1}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className={`text-sm font-semibold truncate ${item.is_usable ? 'text-slate-800' : 'text-slate-400'}`}>
                                {item.company}
                              </span>
                              {item.is_recommended && <Star size={12} className="text-amber-500 fill-amber-500 flex-shrink-0" />}
                              {item.is_usable && (
                                <Badge className="bg-emerald-100 text-emerald-700 text-[8px]">Utilizzabile</Badge>
                              )}
                              {!item.is_usable && (
                                <Badge className="bg-slate-200 text-slate-500 text-[8px]">Non utilizzabile</Badge>
                              )}
                              {item.has_convention && (
                                <Badge className="bg-purple-100 text-purple-700 text-[8px]">
                                  <Tag size={8} className="mr-0.5" />{item.convention_name || 'Convenzione'}
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              {item.discount_code && (
                                <span className="text-[10px] text-blue-600 font-mono bg-blue-50 px-1.5 py-0.5 rounded">
                                  {item.discount_code}
                                </span>
                              )}
                              {item.discount && <span className="text-[10px] text-purple-600">Sconto: {item.discount}</span>}
                              {item.clauses?.length > 0 && (
                                <span className="text-[10px] text-slate-500">Clausole: {item.clauses.join(', ')}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 ml-9 sm:ml-0">
                          <div className="text-right">
                            <p className={`text-lg font-bold ${item.is_usable ? 'text-slate-900' : 'text-slate-400'}`}>
                              {item.price?.toFixed(2)} <span className="text-xs font-normal text-slate-500">EUR</span>
                            </p>
                            <p className="text-[10px] text-slate-400">annuo</p>
                          </div>
                        </div>
                      </div>
                    ))}
                    {getDisplayResults().length === 0 && (
                      <div className="text-center py-10 text-slate-400 text-sm">
                        {filterCompany || filterUsable ? 'Nessun risultato con i filtri applicati' : 'Nessun preventivo disponibile'}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-4 flex justify-end gap-2">
                  <Button variant="outline" onClick={() => { setStep(0); setResults(null); }} className="h-9 text-xs">
                    Nuovo Preventivo
                  </Button>
                  {results.request_id && (
                    <Button onClick={() => onComplete(results.request_id)} className="h-9 text-xs bg-blue-600 hover:bg-blue-700" data-testid="view-practice-btn">
                      <Eye size={14} className="mr-1" /> Vai alla Pratica
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Navigation ── */}
        {step < 6 && (
          <div className="flex justify-between mt-6 pt-4 border-t border-slate-100">
            <div>
              {step > 0 && (
                <Button variant="outline" onClick={() => setStep(step - 1)} className="h-9 text-xs" data-testid="wizard-back-btn">
                  <ChevronLeft size={14} className="mr-1" /> Indietro
                </Button>
              )}
              {step === 0 && (
                <Button variant="ghost" onClick={onCancel} className="h-9 text-xs text-slate-500" data-testid="wizard-cancel-btn">Annulla</Button>
              )}
            </div>
            {step < 5 ? (
              <Button onClick={() => setStep(step + 1)} className="h-9 text-xs bg-blue-600 hover:bg-blue-700" data-testid="wizard-next-btn">
                Avanti <ChevronRight size={14} className="ml-1" />
              </Button>
            ) : (
              <Button onClick={handleSubmit} disabled={submitting} className="h-9 text-xs bg-emerald-600 hover:bg-emerald-700" data-testid="submit-quote-btn">
                {submitting ? <Loader2 size={14} className="animate-spin mr-1" /> : <Search size={14} className="mr-1" />}
                {submitting ? 'Calcolo in corso...' : 'Calcola Preventivi'}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════
//  PRACTICE DETAIL COMPONENT
// ══════════════════════════════════════
const PracticeDetail = ({ requestId, onBack }) => {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recalculating, setRecalculating] = useState(false);
  const [users, setUsers] = useState([]);

  const load = useCallback(async () => {
    try {
      const [res, usersRes] = await Promise.all([
        api.get(`/auto-quotes/${requestId}`),
        api.get('/users'),
      ]);
      setData(res.data);
      setUsers(usersRes.data || []);
    } catch { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  }, [requestId]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (status) => {
    try {
      await api.post(`/auto-quotes/${requestId}/change-status`, { status });
      toast.success('Stato aggiornato');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };

  const assignTo = async (userId) => {
    try {
      await api.post(`/auto-quotes/${requestId}/assign`, { user_id: userId });
      toast.success('Pratica assegnata');
      load();
    } catch { toast.error('Errore assegnazione'); }
  };

  const recalculate = async () => {
    setRecalculating(true);
    try {
      await api.post(`/auto-quotes/run-internal-estimate/${requestId}`);
      toast.success('Preventivi ricalcolati');
      load();
    } catch { toast.error('Errore ricalcolo'); }
    finally { setRecalculating(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin text-blue-500" /></div>;
  if (!data) return <p className="text-center text-slate-500 py-10">Pratica non trovata</p>;

  const { request: req, customer: cust, vehicle: veh, results, status_logs } = data;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack} className="h-8 text-xs"><ChevronLeft size={14} className="mr-1" /> Lista</Button>
          <div>
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              {req.request_code}
              <Badge className={`${STATUS_COLORS[req.status] || 'bg-slate-100 text-slate-600'} text-[10px]`}>
                {STATUSES.find(s => s.value === req.status)?.label || req.status}
              </Badge>
            </h2>
            <p className="text-xs text-slate-500">{cust?.first_name} {cust?.last_name} - {veh?.plate}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Select onValueChange={changeStatus}>
            <SelectTrigger className="h-8 w-[160px] text-[11px]"><SelectValue placeholder="Cambia stato..." /></SelectTrigger>
            <SelectContent>{STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
          {isAdmin && (
            <Select onValueChange={assignTo}>
              <SelectTrigger className="h-8 w-[150px] text-[11px]"><UserPlus size={12} className="mr-1" /><SelectValue placeholder="Assegna a..." /></SelectTrigger>
              <SelectContent>{users.map(u => <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button size="sm" variant="outline" onClick={recalculate} disabled={recalculating} className="h-8 text-[11px]">
            {recalculating ? <Loader2 size={12} className="animate-spin mr-1" /> : <RefreshCw size={12} className="mr-1" />} Ricalcola
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-white rounded-lg border border-slate-200 p-3">
          <h4 className="text-[11px] font-bold text-slate-500 mb-2 flex items-center gap-1"><User size={12} /> CLIENTE</h4>
          <p className="text-sm font-semibold text-slate-900">{cust?.first_name} {cust?.last_name}</p>
          <p className="text-xs text-slate-600">{cust?.phone} | {cust?.email}</p>
          <p className="text-xs text-slate-500">{cust?.address}, {cust?.city} ({cust?.province})</p>
          {cust?.birth_date && <p className="text-[10px] text-slate-400">Nato: {cust.birth_date}</p>}
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-3">
          <h4 className="text-[11px] font-bold text-slate-500 mb-2 flex items-center gap-1"><Car size={12} /> VEICOLO</h4>
          <p className="text-sm font-mono font-bold text-slate-900">{veh?.plate}</p>
          <p className="text-xs text-slate-600">{veh?.make} {veh?.model} {veh?.version}</p>
          <p className="text-xs text-slate-500">{veh?.fuel_type} - {veh?.power_kw} kW - {veh?.displacement_cc} cc</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-3">
          <h4 className="text-[11px] font-bold text-slate-500 mb-2 flex items-center gap-1"><Shield size={12} /> POLIZZA</h4>
          <p className="text-sm font-semibold text-slate-900">{req.cu_class}a classe CU</p>
          <p className="text-xs text-slate-600">Sinistri: {req.claims_last_5y} | Bersani: {req.bersani_applied ? 'Si' : 'No'}</p>
          {req.current_company && <p className="text-xs text-slate-500">Attuale: {req.current_company}</p>}
          <p className="text-[10px] text-slate-400">Assegnata a: {data.assigned_user_name || '-'}</p>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h4 className="text-sm font-bold text-slate-900">Preventivi ({results?.length || 0})</h4>
          {req.estimated_premium_min > 0 && (
            <span className="text-xs text-slate-500">Range: {req.estimated_premium_min?.toFixed(0)} - {req.estimated_premium_max?.toFixed(0)} EUR</span>
          )}
        </div>
        <div className="divide-y divide-slate-50">
          {(results || []).map((r, i) => (
            <div key={r.id || i} className={`px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 ${
              !r.is_usable_by_intermediary ? 'opacity-50 bg-slate-50' : r.is_recommended ? 'bg-emerald-50/50' : ''
            }`}>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="text-[10px] font-bold text-slate-400 w-5">{i + 1}.</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-semibold text-slate-800 truncate">{r.company_name}</span>
                    {r.is_recommended && <Star size={12} className="text-amber-500 fill-amber-500 flex-shrink-0" />}
                    {!r.is_usable_by_intermediary && <Badge className="bg-red-100 text-red-600 text-[8px]">Non utilizzabile</Badge>}
                  </div>
                  <p className="text-[10px] text-slate-500">{r.product_name} - {r.coverage_summary}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 sm:gap-4 ml-7 sm:ml-0">
                <div className="text-right">
                  <p className="text-base font-bold text-slate-900">{r.annual_premium?.toFixed(2)} EUR</p>
                  <p className="text-[10px] text-slate-500">annuo</p>
                </div>
                {r.installment_premium > 0 && (
                  <div className="text-right">
                    <p className="text-xs text-slate-600">{r.installment_premium?.toFixed(2)} EUR</p>
                    <p className="text-[10px] text-slate-400">{r.installment_frequency}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
          {(!results || results.length === 0) && (
            <p className="text-center text-slate-400 py-6 text-sm">Nessun preventivo calcolato</p>
          )}
        </div>
      </div>

      {status_logs?.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <h4 className="text-xs font-bold text-slate-500 mb-2">Storico</h4>
          <div className="space-y-1.5">
            {status_logs.map((l, idx) => (
              <div key={l.id || idx} className="flex items-center gap-2 text-[11px]">
                <Clock size={10} className="text-slate-400 flex-shrink-0" />
                <span className="text-slate-400">{l.created_at?.substring(0, 16).replace('T', ' ')}</span>
                <Badge className={`${STATUS_COLORS[l.new_status] || 'bg-slate-100'} text-[9px]`}>
                  {STATUSES.find(s => s.value === l.new_status)?.label || l.new_status}
                </Badge>
                {l.change_reason && <span className="text-slate-500">- {l.change_reason}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════
//  MAIN PAGE COMPONENT
// ══════════════════════════════════════
export const PreventivatoreAuto = () => {
  const [view, setView] = useState('dashboard');
  const [requests, setRequests] = useState([]);
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedRequestId, setSelectedRequestId] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [reqRes, kpiRes] = await Promise.all([
        api.get('/auto-quotes', { params: { status: filterStatus || undefined, search: searchQuery || undefined } }),
        api.get('/auto-quotes/dashboard/kpi'),
      ]);
      setRequests(reqRes.data.requests || []);
      setKpi(kpiRes.data);
    } catch {}
    finally { setLoading(false); }
  }, [filterStatus, searchQuery]);

  useEffect(() => { loadData(); }, [loadData]);

  const openDetail = (id) => { setSelectedRequestId(id); setView('detail'); };
  const openWizard = () => setView('wizard');
  const backToDashboard = () => { setView('dashboard'); loadData(); };

  if (view === 'wizard') {
    return (
      <div className="p-4 sm:p-5 md:p-7">
        <QuoteWizard onComplete={(id) => { setSelectedRequestId(id); setView('detail'); }} onCancel={backToDashboard} />
      </div>
    );
  }

  if (view === 'detail' && selectedRequestId) {
    return (
      <div className="p-4 sm:p-5 md:p-7">
        <PracticeDetail requestId={selectedRequestId} onBack={backToDashboard} />
      </div>
    );
  }

  if (view === 'companies') {
    return (
      <div className="p-4 sm:p-5 md:p-7">
        <div className="flex items-center gap-2 mb-5">
          <Button variant="ghost" size="sm" onClick={backToDashboard} className="h-8 text-xs">
            <ChevronLeft size={14} className="mr-1" /> Pratiche
          </Button>
        </div>
        <GestioneCompagnie />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-5 md:p-7" data-testid="preventivatore-auto-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-5 gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">Preventivatore Auto</h1>
          <p className="text-xs sm:text-sm text-slate-500">Gestione pratiche preventivi RC Auto</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setView('companies')} className="h-9 text-xs" data-testid="manage-companies-btn">
            <Building2 size={14} className="mr-1.5" /> Compagnie
          </Button>
          <Button onClick={openWizard} className="bg-blue-600 hover:bg-blue-700 h-9 text-xs" data-testid="new-quote-btn">
            <Plus size={16} className="mr-1.5" /> Nuovo Preventivo
          </Button>
        </div>
      </div>

      {kpi && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-5">
          {[
            { label: 'Totali', value: kpi.total, color: 'bg-slate-800 text-white' },
            { label: 'In Lavorazione', value: kpi.in_progress, color: 'bg-blue-600 text-white' },
            { label: 'Inviate', value: kpi.preventivi_inviati, color: 'bg-indigo-600 text-white' },
            { label: 'Emesse', value: kpi.emesse, color: 'bg-emerald-600 text-white' },
            { label: 'Perse', value: kpi.perse, color: 'bg-red-600 text-white' },
            { label: 'Conversione', value: `${kpi.tasso_conversione}%`, color: 'bg-amber-500 text-white' },
            { label: 'Premio Medio', value: kpi.premio_medio > 0 ? `${kpi.premio_medio} EUR` : '-', color: 'bg-purple-600 text-white' },
          ].map(k => (
            <div key={k.label} className={`${k.color} rounded-lg p-3 text-center`}>
              <p className="text-lg sm:text-xl font-bold">{k.value}</p>
              <p className="text-[10px] opacity-80">{k.label}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Cerca per targa, nome, telefono, email..." className="pl-9 h-9 text-sm" data-testid="search-quotes" />
        </div>
        <Select value={filterStatus} onValueChange={v => setFilterStatus(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-9 w-full sm:w-[180px] text-xs"><Filter size={12} className="mr-1.5" /><SelectValue placeholder="Tutti gli stati" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="quotes-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500">Codice</th>
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500">Cliente</th>
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 hidden md:table-cell">Targa</th>
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 hidden lg:table-cell">Veicolo</th>
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500">Stato</th>
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 hidden md:table-cell">Premio</th>
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 hidden lg:table-cell">Assegnato</th>
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 hidden sm:table-cell">Data</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {requests.map(r => (
                <tr key={r.id} className="hover:bg-slate-50/50 cursor-pointer transition-colors" onClick={() => openDetail(r.id)}>
                  <td className="px-4 py-2.5">
                    <span className="text-xs font-mono font-semibold text-blue-700">{r.request_code}</span>
                  </td>
                  <td className="px-4 py-2.5">
                    <p className="text-xs font-medium text-slate-800 truncate max-w-[140px]">{r.customer_name}</p>
                    <p className="text-[10px] text-slate-400">{r.customer_phone}</p>
                  </td>
                  <td className="px-4 py-2.5 hidden md:table-cell">
                    <span className="text-xs font-mono font-bold text-slate-700">{r.plate}</span>
                  </td>
                  <td className="px-4 py-2.5 hidden lg:table-cell">
                    <span className="text-xs text-slate-600 truncate max-w-[120px] block">{r.vehicle_desc}</span>
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge className={`${STATUS_COLORS[r.status] || 'bg-slate-100'} text-[9px]`}>
                      {STATUSES.find(s => s.value === r.status)?.label || r.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5 hidden md:table-cell">
                    {r.estimated_premium_min > 0 ? (
                      <span className="text-xs font-semibold text-slate-700">{r.estimated_premium_min?.toFixed(0)} - {r.estimated_premium_max?.toFixed(0)} EUR</span>
                    ) : <span className="text-xs text-slate-400">-</span>}
                  </td>
                  <td className="px-4 py-2.5 hidden lg:table-cell">
                    <span className="text-xs text-slate-500">{r.assigned_user_name || '-'}</span>
                  </td>
                  <td className="px-4 py-2.5 hidden sm:table-cell">
                    <span className="text-[10px] text-slate-400">{r.created_at?.substring(0, 10)}</span>
                  </td>
                  <td className="px-3 py-2.5">
                    <Button variant="ghost" size="icon" className="h-7 w-7"><Eye size={14} className="text-slate-400" /></Button>
                  </td>
                </tr>
              ))}
              {requests.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center py-10 text-slate-400 text-sm">
                    {loading ? <Loader2 size={20} className="animate-spin mx-auto" /> : 'Nessuna pratica trovata'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PreventivatoreAuto;
